// Phase 4 documents (12, 14 §14.9): handovers to partners, partner references and updates, the
// partner update link, Prime pieces and supplier bills. All server-written.
import { z } from 'zod';
import { branchCodeSchema, sectorCodeSchema } from './config';
import { trackingNoSchema, unitIdSchema } from './cargo';

const minor = z.number().int().nonnegative().max(1e12);
const iso = z.string();

export const PARTNER_STATUSES = [
  'received_by_partner',
  'out_for_delivery',
  'failed_attempt',
  'delivered',
  'damaged',
  'lost',
  'returned_to_hub',
] as const;
export const FAILED_REASONS = [
  'not_home',
  'wrong_address',
  'refused',
  'unreachable',
  'other',
] as const;
export const UPDATE_SOURCES = ['console', 'link', 'import', 'api'] as const;

export const HANDOVER_STATUSES = [
  'draft',
  'loading',
  'handed_over',
  'in_progress',
  'completed',
  'closed',
] as const;

/** `handovers/{hnd_…}`: a batch of units given to one partner at one time (12 §12.5). */
export const handoverDocSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  partnerName: z.string(),
  kind: z.enum(['hauler', 'courier']),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  status: z.enum(HANDOVER_STATUSES),
  counts: z
    .object({
      units: z.number().int(),
      delivered: z.number().int(),
      open: z.number().int(),
      problems: z.number().int(),
      returned: z.number().int(),
      /** Units written off after a loss. They count as settled, so the handover can complete. */
      writtenOff: z.number().int().default(0),
    })
    .strict(),
  accruedMinor: minor,
  currency: z.string(),
  representative: z
    .object({
      name: z.string(),
      plate: z.string().default(''),
      signaturePath: z.string().default(''),
      vehiclePhotoPath: z.string().default(''),
    })
    .strict()
    .optional(),
  /** Hash of the partner link token; the token itself is never stored on the handover (12 §12.7). */
  linkTokenHash: z.string().optional(),
  /** Set when the handover completes or closes: 30 days later the link stops working. */
  linkExpiresAt: iso.optional(),
  lastUpdateAt: iso.optional(),
  /** Destination charges the partner collected from consignees, in `currency` (1003 until remitted). */
  collectedMinor: minor.default(0),
  /** The partner-quiet episode (12 §12.7 "Chasing quiet partners"): when it began and how it climbed. */
  quiet: z
    .object({
      startedAt: iso,
      chaseCount: z.number().int(),
      lastChaseAt: iso,
      unitIds: z.array(z.string()),
      taskRaisedAt: iso.optional(),
      escalatedAt: iso.optional(),
    })
    .strict()
    .optional(),
  completedAt: iso.optional(),
  createdBy: z.string().optional(),
  commandId: z.string().optional(),
  stateSince: iso,
  nextDueAt: iso.optional(),
  handedOverAt: iso.optional(),
  closedAt: iso.optional(),
  closeReason: z.string().optional(),
  createdAt: iso,
});

/** `handovers/{id}/items/{unitId}` */
export const handoverItemDocSchema = z.object({
  unitId: unitIdSchema,
  trackingNo: trackingNoSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  cityId: z.string(),
  sizeId: z.string().optional(),
  weightKg: z.number().optional(),
  accruedMinor: minor,
  currency: z.string(),
  /** `box`, or a Prime `piece`. */
  kind: z.enum(['box', 'piece']).default('box'),
  /** The destination country, which a courier prices a piece by. */
  country: z.string().optional(),
  partnerId: z.string().optional(),
  handoverId: z.string().optional(),
  partnerStatus: z.enum(PARTNER_STATUSES).optional(),
  partnerRefs: z.array(z.string()).default([]),
  lastUpdateAt: iso.optional(),
  /** The first update after the handover itself, for the partner's responsiveness. */
  firstUpdateAt: iso.optional(),
  /** Reported damaged or lost at some point (kept for the scorecard even if it was delivered later). */
  damaged: z.boolean().optional(),
  lost: z.boolean().optional(),
  deliveredAt: iso.optional(),
  /** False when the delivery had no code or a wrong one (12 §12.5 step 6). */
  deliveryVerified: z.boolean().optional(),
  returnedAt: iso.optional(),
  /** How the unit left the partner's hands: delivered, returned to a branch, or written off. */
  settled: z.enum(['delivered', 'returned', 'written_off']).optional(),
  /** The city's target days and the date the partner should have delivered by. */
  targetDays: z.number().int().optional(),
  dueAt: iso.optional(),
  /** No rate was found for this unit, so nothing was accrued. */
  noRate: z.boolean().optional(),
  billId: z.string().optional(),
  billedMinor: minor.optional(),
  conditionNote: z.string().default(''),
  addedAt: iso,
});

/** `partnerUpdates/{pup_…}`: every update as received, for audit (12 §12.7). */
export const partnerUpdateDocSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  handoverId: z.string(),
  unitIds: z.array(z.string()),
  status: z.enum(PARTNER_STATUSES),
  at: iso,
  source: z.enum(UPDATE_SOURCES),
  enteredBy: z.string(),
  receiverName: z.string().default(''),
  deliveryCodeOk: z.boolean().optional(),
  /** False when a delivery came with no code, or the wrong one. */
  deliveryVerified: z.boolean().optional(),
  podPaths: z.array(z.string()).default([]),
  collectedMinor: minor.optional(),
  collectedCurrency: z.string().optional(),
  reason: z.enum(FAILED_REASONS).optional(),
  note: z.string().default(''),
  original: z.string().default(''),
  /** Units the update named but the server did not touch, with why. */
  refused: z
    .array(z.object({ unitId: z.string(), code: z.string(), message: z.string() }).strict())
    .default([]),
  /** `partner:{handoverId}`, `adapter:{partnerId}` or `staff:{uid}`. */
  principal: z.string().optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  commandId: z.string(),
  createdAt: iso,
});

/** `bills/{bil_…}`: a supplier bill, matched line by line against accruals (12 §12.6, 14). */
export const billDocSchema = z.object({
  id: z.string(),
  supplierKind: z.enum(['partner', 'other']),
  partnerId: z.string().optional(),
  supplierName: z.string(),
  billNo: z.string(),
  billDate: z.iso.date(),
  dueDate: z.iso.date(),
  currency: z.string(),
  totalMinor: minor,
  paidMinor: minor,
  status: z.enum(['recorded', 'awaiting_approval', 'approved', 'part_paid', 'paid', 'rejected']),
  match: z
    .object({
      matchedMinor: minor,
      accruedMinor: minor,
      differenceMinor: z.number().int(),
      lines: z.number().int(),
      problems: z.array(
        z
          .object({
            unitId: z.string().optional(),
            kind: z.enum([
              'not_handed_over',
              'billed_twice',
              'rate_differs',
              'not_delivered',
              'weight_variance',
              'not_billed',
            ]),
            message: z.string(),
            billedMinor: minor.optional(),
            accruedMinor: minor.optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  link: z
    .object({
      kind: z.enum(['handover', 'container', 'trip', 'branch', 'partner']),
      id: z.string(),
    })
    .strict()
    .optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  createdAt: iso,
  /** How each billed line matched, kept for the bill screen: unit, amount and the problem kinds. */
  lines: z
    .array(
      z.object({
        unitId: z.string().optional(),
        trackingNo: z.string().optional(),
        description: z.string().default(''),
        amountMinor: minor,
        weightKg: z.number().optional(),
        problems: z.array(z.string()).default([]),
      }),
    )
    .default([]),
  category: z.string().optional(),
  note: z.string().default(''),
  filePath: z.string().optional(),
  commandId: z.string().optional(),
  createdBy: z.string().optional(),
  approvalId: z.string().optional(),
  journalId: z.string().optional(),
  payments: z
    .array(
      z.object({
        walletId: z.string(),
        amountMinor: minor,
        reference: z.string().default(''),
        at: iso,
        journalId: z.string(),
        commandId: z.string(),
        by: z.string(),
      }),
    )
    .default([]),
  /** Reserve released by this bill's payments, per allocation (14 §14.6). */
  reserveReleases: z.record(z.string(), z.number().int()).default({}),
  /** The shipments whose boxes this partner bill cleared, with how many each (for the reserve release). */
  billedShipments: z.record(z.string(), z.number().int()).default({}),
});

// ---- The partner update link (12 §12.7, §12.9) ---------------------------------

export const LINK_LIMITS = {
  maxUnitsPerUpdate: 200,
  maxUpdatesPerRequest: 20,
  maxPhotosPerUpdate: 5,
  /** After base64 decoding. The app compresses on the phone first. */
  maxPhotoBytes: 512_000,
  requestsPerMinute: 60,
  /** The link works this many days after the handover completes or closes. */
  daysAfterClose: 30,
} as const;

export const partnerLinkPhotoSchema = z
  .object({
    contentType: z.literal('image/jpeg'),
    base64: z
      .string()
      .min(16)
      .max(Math.ceil((LINK_LIMITS.maxPhotoBytes * 4) / 3) + 8),
  })
  .strict();

/** One tap in the partner's page: the idempotency key is `clientId`. */
export const partnerLinkUpdateSchema = z
  .object({
    clientId: z.string().min(1).max(40),
    unitIds: z.array(unitIdSchema).min(1).max(LINK_LIMITS.maxUnitsPerUpdate),
    status: z.enum(PARTNER_STATUSES),
    at: z.iso.datetime({ offset: true }),
    receiverName: z.string().trim().max(80).default(''),
    deliveryCode: z
      .string()
      .regex(/^\d{4}$/)
      .optional(),
    reason: z.enum(FAILED_REASONS).optional(),
    note: z.string().max(500).default(''),
    collectedMinor: minor.optional(),
    photos: z.array(partnerLinkPhotoSchema).max(LINK_LIMITS.maxPhotosPerUpdate).default([]),
  })
  .strict();

export const partnerLinkPostSchema = z
  .object({
    /** Only when the app cannot set a header (a background resend). Never in the URL. */
    token: z.string().min(16).max(64).optional(),
    updates: z.array(partnerLinkUpdateSchema).min(1).max(LINK_LIMITS.maxUpdatesPerRequest),
  })
  .strict();
export type PartnerLinkUpdate = z.infer<typeof partnerLinkUpdateSchema>;
export type PartnerLinkPost = z.infer<typeof partnerLinkPostSchema>;

export const partnerLinkCountsSchema = z
  .object({
    units: z.number().int(),
    delivered: z.number().int(),
    open: z.number().int(),
    problems: z.number().int(),
    returned: z.number().int(),
  })
  .strict();

export const partnerLinkUnitSchema = z
  .object({
    unitId: unitIdSchema,
    kind: z.enum(['box', 'piece']),
    trackingNo: trackingNoSchema,
    boxNo: z.number().int().positive(),
    boxCount: z.number().int().positive(),
    consigneeFirstName: z.string(),
    city: z.string(),
    barangay: z.string(),
    partnerStatus: z.enum(PARTNER_STATUSES),
    lastUpdateAt: iso.nullable(),
    deliveredAt: iso.nullable(),
    partnerRefs: z.array(z.string()),
    deliveryCodeRequired: z.boolean(),
    collect: z.object({ amountMinor: minor, currency: z.string() }).strict().nullable(),
  })
  .strict();

export const partnerLinkViewSchema = z
  .object({
    ok: z.literal(true),
    serverTime: iso,
    handover: z
      .object({
        id: z.string(),
        partnerName: z.string(),
        status: z.enum(HANDOVER_STATUSES),
        handedOverAt: iso.nullable(),
        expiresAt: iso.nullable(),
        acceptingUpdates: z.boolean(),
        branchName: z.string(),
        currency: z.string(),
        counts: partnerLinkCountsSchema,
      })
      .strict(),
    units: z.array(partnerLinkUnitSchema).max(500),
    limits: z.object({
      maxUnitsPerUpdate: z.number().int(),
      maxUpdatesPerRequest: z.number().int(),
      maxPhotosPerUpdate: z.number().int(),
      maxPhotoBytes: z.number().int(),
      requestsPerMinute: z.number().int(),
    }),
  })
  .strict();
export type PartnerLinkView = z.infer<typeof partnerLinkViewSchema>;

export const partnerLinkResultSchema = z.union([
  z
    .object({
      clientId: z.string(),
      ok: z.literal(true),
      repeat: z.boolean().optional(),
      commandId: z.string(),
      commandStatus: z.enum(['applied', 'applied_with_exception']),
      updateId: z.string().nullable(),
      applied: z.array(z.string()),
      refused: z.array(z.object({ unitId: z.string(), code: z.string(), message: z.string() })),
      deliveryCodeOk: z.boolean().nullable(),
      podPaths: z.array(z.string()),
    })
    .strict(),
  z
    .object({
      clientId: z.string(),
      ok: z.literal(false),
      repeat: z.boolean().optional(),
      commandId: z.string().optional(),
      commandStatus: z.literal('rejected').optional(),
      code: z.string(),
      message: z.string(),
    })
    .strict(),
]);
export type PartnerLinkResult = z.infer<typeof partnerLinkResultSchema>;

export const partnerLinkPostResponseSchema = z
  .object({
    ok: z.literal(true),
    results: z.array(partnerLinkResultSchema),
    handover: z
      .object({ status: z.enum(HANDOVER_STATUSES), counts: partnerLinkCountsSchema })
      .strict(),
  })
  .strict();
export type PartnerLinkPostResponse = z.infer<typeof partnerLinkPostResponseSchema>;

export const partnerLinkErrorSchema = z
  .object({ ok: z.literal(false), code: z.string(), message: z.string() })
  .strict();
export type PartnerLinkError = z.infer<typeof partnerLinkErrorSchema>;

// ---- importPartnerUpdates (a callable, 12 §12.7 channel 2) ------------------------

/** Zero-based column indexes. */
export const importMappingSchema = z
  .object({
    unit: z.number().int().min(0).max(200).optional(),
    trackingNo: z.number().int().min(0).max(200).optional(),
    ref: z.number().int().min(0).max(200).optional(),
    status: z.number().int().min(0).max(200).optional(),
    at: z.number().int().min(0).max(200).optional(),
    receiver: z.number().int().min(0).max(200).optional(),
    note: z.number().int().min(0).max(200).optional(),
    collected: z.number().int().min(0).max(200).optional(),
  })
  .strict();
export type ImportMapping = z.infer<typeof importMappingSchema>;

export const importPartnerUpdatesInputSchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('text'),
      partnerId: z.string().min(1).max(40),
      text: z.string().min(1).max(20_000),
    })
    .strict(),
  z
    .object({
      mode: z.literal('csv'),
      partnerId: z.string().min(1).max(40),
      rows: z
        .array(z.array(z.string().max(500)).max(60))
        .min(1)
        .max(2000),
      hasHeader: z.boolean().default(true),
      mapping: importMappingSchema.optional(),
      saveMapping: z.boolean().default(false),
    })
    .strict(),
]);
export type ImportPartnerUpdatesInput = z.input<typeof importPartnerUpdatesInputSchema>;

export const partnerProposalSchema = z
  .object({
    key: z.string(),
    handoverId: z.string(),
    unitIds: z.array(unitIdSchema).min(1),
    status: z.enum(PARTNER_STATUSES),
    at: iso,
    receiverName: z.string(),
    reason: z.enum(FAILED_REASONS).nullable(),
    note: z.string(),
    collectedMinor: minor.nullable(),
    original: z.string(),
    matchedBy: z.array(z.string()),
    warnings: z.array(z.string()),
  })
  .strict();
export type PartnerProposal = z.infer<typeof partnerProposalSchema>;

export const importPartnerUpdatesOutputSchema = z
  .object({
    proposals: z.array(partnerProposalSchema),
    unmatched: z.array(z.object({ text: z.string(), why: z.string() }).strict()),
    mapping: importMappingSchema.nullable(),
    hasHeader: z.boolean().nullable(),
  })
  .strict();
export type ImportPartnerUpdatesOutput = z.infer<typeof importPartnerUpdatesOutputSchema>;

/** `partnerImportMaps/{partnerId}`: the column map remembered per partner. */
export const partnerImportMapDocSchema = z.object({
  partnerId: z.string(),
  mapping: importMappingSchema,
  hasHeader: z.boolean(),
  updatedBy: z.string(),
  updatedAt: iso,
});

// ---- Scorecards (12 §12.4) ------------------------------------------------------

/** `partnerScorecards/{partnerId}`: rolling 90 days, computed daily by the control tower. */
export const partnerScorecardDocSchema = z.object({
  partnerId: z.string(),
  partnerName: z.string(),
  /** The first branch's country, for the rules; the card is per partner. */
  sectorId: sectorCodeSchema,
  branchId: branchCodeSchema,
  windowDays: z.number().int(),
  from: iso,
  to: iso,
  unitsHandedOver: z.number().int(),
  delivered: z.number().int(),
  /** Percent, one decimal. Null when there is nothing to measure yet. */
  onTimePct: z.number().nullable(),
  avgDays: z.number().nullable(),
  damageLossRatePct: z.number().nullable(),
  responsivenessPct: z.number().nullable(),
  openUnits: z.number().int(),
  damaged: z.number().int(),
  lost: z.number().int(),
  computedAt: iso,
});
export type PartnerScorecardDoc = z.infer<typeof partnerScorecardDocSchema>;
